import React, {useEffect, useId, useRef, useState} from "react";
import {Button, OverlayTrigger, Tooltip} from "react-bootstrap";

const HANDY_ABFRAGE = "(max-width: 991.98px)";
const HOEHEN_VARIABLE = "--sticky-filter-hoehe";

function useIstHandy() {
    const [istHandy, setIstHandy] = useState(() => typeof window !== "undefined" && window.matchMedia(HANDY_ABFRAGE).matches);
    useEffect(() => {
        const abfrage = window.matchMedia(HANDY_ABFRAGE);
        const aktualisieren = () => setIstHandy(abfrage.matches);
        abfrage.addEventListener("change", aktualisieren);
        return () => abfrage.removeEventListener("change", aktualisieren);
    }, []);
    return istHandy;
}

/** true, sobald der Messpunkt oben aus dem sichtbaren Bereich geschoben ist. */
function useFixiert(messpunktRef, abhaengigkeit) {
    const [fixiert, setFixiert] = useState(false);
    useEffect(() => {
        const messpunkt = messpunktRef.current;
        if (!messpunkt || typeof IntersectionObserver === "undefined") {
            return undefined;
        }
        const beobachter = new IntersectionObserver(
            ([eintrag]) => setFixiert(!eintrag.isIntersecting && eintrag.boundingClientRect.top < 1),
            {threshold: [0, 1]}
        );
        beobachter.observe(messpunkt);
        return () => beobachter.disconnect();
    }, [messpunktRef, abhaengigkeit]);
    return fixiert;
}

/** Schreibt die Höhe des fixierten Elements als CSS-Variable ans Dokument. */
function useHoehenVariable(elementRef, abhaengigkeit) {
    useEffect(() => {
        const element = elementRef.current;
        const wurzel = document.documentElement;
        if (!element || typeof ResizeObserver === "undefined") {
            return undefined;
        }
        const beobachter = new ResizeObserver(() => {
            wurzel.style.setProperty(HOEHEN_VARIABLE, `${Math.ceil(element.getBoundingClientRect().height)}px`);
        });
        beobachter.observe(element);
        return () => {
            beobachter.disconnect();
            wurzel.style.removeProperty(HOEHEN_VARIABLE);
        };
    }, [elementRef, abhaengigkeit]);
}

/**
 * Fixierter Filterblock (Rückmeldung André Bruland, 28.09.2026, Paket 1):
 * Die Filter bleiben beim Scrollen erreichbar, Zweckkasten und Überschrift
 * scrollen weg. Wiederverwendbar für jede Seite mit Filterleiste und langer
 * Tabelle.
 *
 * - Desktop (ab lg): Der ganze Block (Filterleiste, Datenstand) ist sticky
 *   am oberen Rand des Scrollbereichs, weißer Hintergrund, leichter Schatten,
 *   sobald er tatsächlich fixiert ist (erkannt über einen unsichtbaren
 *   Messpunkt direkt davor).
 * - Handy (unter lg): Die Filter stehen normal im Fluss und scrollen weg;
 *   fixiert bleibt nur eine schmale Zusammenfassungszeile (`summary`), die
 *   erst erscheint, wenn die Filter aus dem Bild sind. Ein Tipp auf „Filter“
 *   klappt die vollständigen Filter als Tafel unter der Zeile auf; an ihrer
 *   alten Stelle bleibt ein Platzhalter gleicher Höhe, damit die Seite nicht
 *   springt. So ändert sich die Seitenhöhe beim Fixieren nie nach unten -
 *   ein Schrumpfen des fixierten Blocks hätte die Seite kürzer gemacht und
 *   den Zustand hin- und herkippen lassen.
 * - Die Höhe des fixierten Teils steht als CSS-Variable --sticky-filter-hoehe
 *   am Dokument. StickyTableArea rechnet damit die Höhe des
 *   Tabellenbereichs, damit der Tabellenkopf darunter sichtbar bleibt.
 *
 * @param {React.ReactNode} children - Filterleiste, Datenstand usw.
 * @param {string} [summary] - Kurzfassung der gewählten Filter für die Handy-Zeile.
 */
export default function StickyFilterBlock({children, summary}) {
    const istHandy = useIstHandy();
    return istHandy
        ? <HandyFilterBlock summary={summary}>{children}</HandyFilterBlock>
        : <DesktopFilterBlock>{children}</DesktopFilterBlock>;
}

function DesktopFilterBlock({children}) {
    const messpunktRef = useRef(null);
    const blockRef = useRef(null);
    const fixiert = useFixiert(messpunktRef);
    useHoehenVariable(blockRef);

    return (
        <>
            <div ref={messpunktRef} className="sticky-filter-messpunkt" aria-hidden="true" />
            <div ref={blockRef} className={"sticky-filter-block" + (fixiert ? " is-fixiert" : "")}>
                {children}
            </div>
        </>
    );
}

function HandyFilterBlock({children, summary}) {
    const inhaltRef = useRef(null);
    const messpunktRef = useRef(null);
    const zeileRef = useRef(null);
    const [aufgeklappt, setAufgeklappt] = useState(false);
    const [platzhalterHoehe, setPlatzhalterHoehe] = useState(0);
    const fixiert = useFixiert(messpunktRef);
    const tafelId = useId();
    useHoehenVariable(zeileRef);

    useEffect(() => {
        if (!fixiert) {
            setAufgeklappt(false);
        }
    }, [fixiert]);

    const aufklappen = () => {
        if (!aufgeklappt && inhaltRef.current) {
            setPlatzhalterHoehe(inhaltRef.current.getBoundingClientRect().height);
        }
        setAufgeklappt((wert) => !wert);
    };

    const inTafel = fixiert && aufgeklappt;

    return (
        <>
            {inTafel
                ? <div style={{height: platzhalterHoehe}} aria-hidden="true" />
                : <div ref={inhaltRef}>{children}</div>}
            <div ref={messpunktRef} className="sticky-filter-messpunkt" aria-hidden="true" />
            <div ref={zeileRef} className={"sticky-filter-block sticky-filter-handy" + (fixiert ? " is-fixiert" : "")}>
                {fixiert && (
                    <div className="sticky-filter-zusammenfassung d-flex flex-nowrap align-items-center gap-2">
                        <span className="small text-truncate flex-grow-1" style={{minWidth: 0}}>{summary || "Filter"}</span>
                        <OverlayTrigger
                            placement="bottom"
                            overlay={<Tooltip>{aufgeklappt ? "Filter wieder einklappen." : "Alle Filter aufklappen, um sie zu ändern."}</Tooltip>}
                        >
                            <Button size="sm" variant="outline-secondary" aria-expanded={aufgeklappt} aria-controls={tafelId} onClick={aufklappen}>
                                {aufgeklappt ? "Einklappen" : "Filter"}
                            </Button>
                        </OverlayTrigger>
                    </div>
                )}
                {inTafel && (
                    <div id={tafelId} className="sticky-filter-tafel">
                        {children}
                    </div>
                )}
            </div>
        </>
    );
}

/**
 * Scrollbereich für Tabellen unter einem StickyFilterBlock: ersetzt
 * `.table-responsive`. Dort stand der Tabellenkopf (thead.sticky-top) in
 * einem Bereich mit overflow-x: auto und fixierte deshalb nicht. Hier ist
 * der Bereich selbst senkrecht begrenzt (Bildschirmhöhe minus fixierter
 * Filterteil), waagerecht bleibt er wischbar, und der Kopf bleibt oben stehen.
 */
export function StickyTableArea({children, className = ""}) {
    return <div className={"sticky-table-area " + className}>{children}</div>;
}
